import { Component } from '@theme/component';
import { isWishlisted, toggleWishlist, WishlistUpdateEvent } from '@theme/wishlist';

/**
 * A heart toggle that saves/removes a single product from the theme-native wishlist.
 * Used on product cards (overlaid on the media), on the product page (inline, with a label),
 * and inside the wishlist page's own cards (acts as "remove").
 *
 * @extends {Component}
 */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    this.addEventListener('click', this.onActivate);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);

    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.onActivate);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  get handle() {
    return this.dataset.handle ?? '';
  }

  /**
   * @param {MouseEvent} event
   */
  onActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.handle) return;
    toggleWishlist(this.handle);
  };

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.onActivate(event);
  };

  /**
   * @param {WishlistUpdateEvent} event
   */
  onWishlistUpdate = (event) => {
    if (event.detail.changedHandle !== this.handle) return;
    this.render();
  };

  render() {
    const active = this.handle ? isWishlisted(this.handle) : false;

    this.classList.toggle('wishlist-button--active', active);
    this.setAttribute('aria-pressed', String(active));

    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.setAttribute('aria-label', label);

    const labelEl = this.querySelector('.wishlist-button__label');
    if (labelEl && label) labelEl.textContent = label;
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
